import Axios from 'axios'
import Phrase from '../../libs/translationsUtils/phrase'
import { Component, h } from 'preact'
import Message from '../design-system-components/message'
import localStorageCache from '../../utils/localStorageCache'
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../design-system-components/modal/index'
import Button from '../../control-components/ui-components/Button'
import { Message as InfoMessage } from '../../control-components/ui-components/Message'
import { subscribe } from '../../utils/globals'

type Props = ({
  groupTitle?: undefined
  groupId?: undefined
  token?: undefined
} | {
  groupTitle: string
  groupId: number
  token: string
}) & {
  publicGroupMessage?: boolean
}

interface State {
  groupTitleAfterJoin?: string
  closedModal?: boolean
  showPublicGroupMessage?: undefined | 'topic' | 'reply'
  groupId?: number
}

const TOKEN_STORE = 'group_invite_token'
const PUBLIC_GROUP_TYPE = 4

interface GroupJoinItem {
  id: number
  token?: string
}

interface GroupJoinWithTokenItem extends GroupJoinItem{
  token: string
}

function hasGroupId (item: unknown): item is GroupJoinItem {
  return item instanceof Object && 'id' in item
}

function hasToken (item: unknown): item is GroupJoinWithTokenItem {
  return item instanceof Object && 'token' in item
}

type CategorySelectedEvent = CustomEvent<{
  id: number
  label: string
  types: number[]
}>

type TopicNewEvent = CustomEvent<{
  category: number
}>

function isCategorySelected (event: unknown): event is CategorySelectedEvent {
  return event instanceof CustomEvent && event.detail.id
}

function isTopicNew (event: unknown): event is TopicNewEvent {
  return event instanceof CustomEvent && event.detail.category
}

export default class GroupJoin extends Component<Props, State> {
  subscription?: () => void

  constructor () {
    super()
    this.proceedWithJoining = this.proceedWithJoining.bind(this)
    this.handleCategorySelected = this.handleCategorySelected.bind(this)
    this.handleTopicNew = this.handleTopicNew.bind(this)
    this.handleEditorSubmit = this.handleEditorSubmit.bind(this)
  }

  isUserLoggedIn (): boolean {
    return !!window.inSidedData.user.userid
  }

  isValidRole (): boolean {
    return window.inSidedData.user.mainRole !== 'roles.not-activated' && window.inSidedData.user.mainRole !== 'roles.requires-approval'
  }

  shouldJoinPublicGroup (): boolean {
    return ['roles.super-user', 'roles.registered'].includes(window.inSidedData.user.mainRole)
  }

  proceedWithJoining (): void {
    localStorageCache.setItem(TOKEN_STORE, {
      id: this.props.groupId,
      token: this.props.token
    })
    this.setState({ closedModal: true })
    window.dispatchEvent(new window.CustomEvent('GuestUserAction'))
  }

  handleTopicNew (event: Event) {
    if (!isTopicNew(event)) return
    if (this.state.groupId !== event.detail.category) return

    localStorageCache.setItem(TOKEN_STORE, {
      id: this.state.groupId
    })
  }

  async handleCategorySelected (event: Event) {
    if (!isCategorySelected(event)) return

    if (event.detail.types[0] !== PUBLIC_GROUP_TYPE || !event.detail.id) {
      this.setState({ groupId: undefined, showPublicGroupMessage: undefined })
      return
    }

    if (await this.isMemberOf(event.detail.id)) return

    this.setState({ groupId: event.detail.id, showPublicGroupMessage: 'topic' })
  }

  async isMemberOf (groupId: number): Promise<boolean> {
    if (groupId <= 0) return false
    const res = await Axios.get(`/group/${groupId}/isMember`)
    return !!res.data
  }

  handleEditorSubmit () {
    if (window.inSidedData.content.category.type !== 'group') return
    localStorageCache.setItem(TOKEN_STORE, {
      id: window.inSidedData.content.category.id
    })
  }

  async showMessageIfNotMember () {
    if (window.inSidedData.content.category.type !== 'group') return
    if (await this.isMemberOf(window.inSidedData.content.category.id)) return
    this.setState({ showPublicGroupMessage: 'reply' })
  }

  componentWillUnmount () {
    window.document.removeEventListener('topic:category_selected', this.handleCategorySelected)
    if (this.subscription) this.subscription()
  }

  async componentDidMount () {
    if (this.props.publicGroupMessage && this.shouldJoinPublicGroup()) {
      window.document.addEventListener('topic:submit', this.handleTopicNew)
      window.document.addEventListener('topic:category_selected', this.handleCategorySelected)

      this.subscription = subscribe('post_editor_submit', this.handleEditorSubmit)
      this.showMessageIfNotMember()
    }

    if (!this.isUserLoggedIn()) return
    if (!this.isValidRole()) return

    const payload = localStorageCache.getItemAndRemove(TOKEN_STORE)

    if (hasToken(payload)) {
      const res = await Axios.post('/groups/joinWithToken', payload)

      this.setState({
        groupTitleAfterJoin: res.data.group.title
      })
      return
    }

    if (hasGroupId(payload)) {
      const res = await Axios.post(`/group/${payload.id}/join`, payload)

      this.setState({
        groupTitleAfterJoin: res.data.group.title
      })
    }
  }

  render () {
    if (this.state.groupTitleAfterJoin) {
      return (
        <Message variant='success' onDismiss={() => this.setState({ groupTitleAfterJoin: undefined })} timer={5000}>
          <Phrase p='groups.invite.joined' count={{ g: this.state.groupTitleAfterJoin }} />
        </Message>
      )
    }

    if (this.props.publicGroupMessage && this.state.showPublicGroupMessage) {
      return (
        <InfoMessage fullWidth>
          <div className='panel-form-text'>
            <Phrase p={`group.autojoin.info.${this.state.showPublicGroupMessage}`} />
          </div>
        </InfoMessage>
      )
    }

    if (this.props.token && !this.state.closedModal) {
      return (
        <Modal onClose={() => this.setState({ closedModal: true })}>
          <ModalHeader title={<Phrase p='groups.invite.modal_title' count={{ g: this.props.groupTitle }} />} />
          <ModalBody><Phrase p='groups.invite.modal_body' /></ModalBody>
          <ModalFooter><Button onClick={this.proceedWithJoining}><Phrase p='groups.invite.modal_confirm' /></Button></ModalFooter>
        </Modal>
      )
    }

    return null
  }
}
